/**
 * API client — thin fetch wrapper.
 *
 * Responsibilities:
 *   - Inject `Authorization: Bearer <token>` from `sessionStorage` on every
 *     request that has a valid token.
 *   - Translate non-OK responses into an `ApiError` carrying the
 *     `ProblemDetails` body. Callers branch on `error.problem.error_code`
 *     (a string from the well-known list) rather than HTTP status.
 *   - Handle 401 globally: clear auth and redirect to /login. The patient
 *     was kicked out; nothing else they can do until they re-authenticate.
 *
 * Non-responsibilities:
 *   - Retry logic (kept at the call site so each endpoint can choose).
 *   - Schema validation (callers downcast; we don't pull Zod into the hot
 *     path for every read). The OpenAPI types in `./types.ts` are the
 *     source of truth.
 *
 * Mocking is layered above this in `mocks/handlers.ts` (MSW) — that
 * intercepts the fetch before it hits the network, so we don't branch on
 * mock-vs-real here.
 */
import type { ProblemDetails } from './types';
import { clearAuth, getValidToken, readRedirectPath } from './storage';
import { stripBasePath, withBasePath } from '../lib/basePath';

export class ApiError extends Error {
  constructor(public readonly problem: ProblemDetails) {
    super(problem.detail || problem.title || 'Request failed');
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  /**
   * If true, sends no Authorization header even when a token is present.
   * Use for the login endpoint; default false.
   */
  unauthenticated?: boolean;
  /**
   * Override the global 401 handler. Default behavior is to clear auth and
   * navigate to /login via window.location. Some callers (e.g. logout
   * itself) want to ignore 401s.
   */
  handle401?: 'redirect' | 'ignore';
}

const DEFAULT_BASE_URL = (() => {
  // Use Vite-style env if present; otherwise fall back to a sensible default
  // so unit tests that import this module without env still work.
  const v = (import.meta as ImportMeta).env?.VITE_API_BASE_URL;
  return typeof v === 'string' && v.length > 0 ? v : 'http://localhost:8000';
})();

export async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const token = options.unauthenticated ? null : getValidToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${DEFAULT_BASE_URL}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (res.status === 401) {
    // Read the body to discover the actual error_code. The previous
    // implementation hard-coded SESSION_EXPIRED here, which meant a
    // PIN_INVALID login response was indistinguishable from a real
    // session timeout — both kicked the user back to /login. That
    // broke the wrong-PIN UX (no error visible to the user) and was
    // surfaced by QA as TC-002/TC-003/TC-004 / B.1.
    //
    // New behavior:
    //   - Read the ProblemDetails body. Use its error_code as the
    //     authoritative discriminator.
    //   - Redirect-to-login only for SESSION_EXPIRED / SESSION_INACTIVE.
    //     These are the only cases where the user is "kicked out" mid-
    //     session and needs to re-authenticate. PIN_INVALID and other
    //     login-time 401s bubble up to the caller (LoginPage) so it
    //     can render the appropriate error inline.
    //   - Never redirect when the user is already on /login (prevents
    //     a redirect loop and lets the login form keep its form state).
    //   - Honor handle401: 'ignore' for callers like logout.
    const problem = await safeReadProblem(res);
    const code = problem.error_code;
    const isSessionEnd =
      code === 'PATIENT_PORTAL_SESSION_EXPIRED' ||
      code === 'PATIENT_PORTAL_SESSION_INACTIVE';
    const isScopeViolation =
      code === 'PATIENT_PORTAL_APPOINTMENT_SCOPE_VIOLATION';
    // Compare app-relative: under a sub-path mount the raw pathname is
    // `/patient-portal/login`, which would never equal '/login' and so
    // would defeat the redirect-loop guard this flag exists for.
    const onLoginPage = stripBasePath(window.location.pathname) === '/login';
    //
    // Magic-link v0.8.0: APPOINTMENT_SCOPE_VIOLATION fires when an
    // appointment-scope JWT attempts to act outside its appointment
    // (e.g. patient lands via appointment-link at /checkin/88001 and
    // then manually navigates the URL bar to /pay). The patient's
    // session is still valid — just scoped. Soft-redirect to the
    // remembered redirect_path from auth time rather than clearing
    // the session and forcing re-auth.
    //
    // Per decision ledger #42, this is the only place the FE acts on
    // appointment scope. We do NOT decode the JWT to gate navigation
    // proactively — backend enforcement + this defensive redirect is
    // sufficient. Per ledger #11, JWT decoding stays out of the FE.
    //
    if (
      options.handle401 !== 'ignore' &&
      isScopeViolation &&
      !onLoginPage
    ) {
      const remembered = readRedirectPath();
      // `redirect_path` comes from the backend and is app-relative, so it
      // compares against the stripped pathname and needs the base path put
      // back on before it goes to `window.location`.
      if (remembered && remembered !== stripBasePath(window.location.pathname)) {
        // Don't clearAuth — session is still valid, just scoped.
        // Hard-navigate to drop in-memory state so the new route's
        // queries refire cleanly.
        window.location.href = withBasePath(remembered);
      }
      throw new ApiError(problem);
    }
    if (
      options.handle401 !== 'ignore' &&
      isSessionEnd &&
      !onLoginPage
    ) {
      clearAuth();
      // Hard-navigate so we drop any in-memory React state. The login page
      // preserves the current path via `?next=`.
      // `?next=` is consumed by react-router `navigate()`, which applies
      // the basename itself — so it must be stored app-relative or the
      // prefix ends up doubled. The `href` is a real browser URL and does
      // need the prefix, or it would leave the portal entirely.
      const next = encodeURIComponent(
        stripBasePath(window.location.pathname) + window.location.search,
      );
      window.location.href = `${withBasePath('/login')}?next=${next}`;
    }
    throw new ApiError(problem);
  }

  if (!res.ok) {
    const problem = await safeReadProblem(res);
    throw new ApiError(problem);
  }

  if (res.status === 204) return undefined as T;
  // Some endpoints return 202 with a body (e.g. /payment/charge); handle
  // both 200 and 202 the same way — read JSON.
  return (await res.json()) as T;
}

async function safeReadProblem(res: Response): Promise<ProblemDetails> {
  try {
    const data = (await res.json()) as Partial<ProblemDetails>;
    return {
      type: data.type ?? 'about:blank',
      title: data.title ?? 'Request failed',
      status: data.status ?? res.status,
      detail: data.detail ?? 'Something went wrong.',
      error_code: data.error_code ?? 'UNKNOWN',
      correlation_id: data.correlation_id ?? '',
      field_errors: data.field_errors,
    };
  } catch {
    return {
      type: 'about:blank',
      title: 'Request failed',
      status: res.status,
      detail: 'Something went wrong. Please try again.',
      error_code: 'UNKNOWN',
      correlation_id: '',
    };
  }
}
